import { createSlice } from '@reduxjs/toolkit';
import { incidentsApi } from '../functions/IncidentsApi';
import { toast } from 'react-toastify';

const initialState = {
  incidentsList: [],
  incidentsPagination: null,
  loading: false,
  selectedIncidentInfo: null,
  incidentInfoFetching: null,
  formSettings: {},
  formSettingsFetching: false,
  formSaveFetching: false,
  departmentsList: {},
};

export const incidentsReducer = createSlice({
  name: 'incidentsReducer',
  initialState,
  reducers: {
    setIncidentsList: (state, action) => {
      state.incidentsList = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setPagination: (state, action) => {
      state.incidentsPagination = action.payload;
    },
    setSelectedIncidentInfo: (state, action) => {
      state.selectedIncidentInfo = action.payload;
    },
    setIncidentInfoFetching: (state, action) => {
      state.incidentInfoFetching = action.payload;
    },
    setDeleteIncident: (state, action) => {
      state.incidentsList = state.incidentsList.filter(
        (item) => item.incident_id !== action.payload
      );
      state.incidentsPagination = {
        ...state.incidentsPagination,
        total: state.incidentsPagination.total - 1,
      };
    },
    setFormSettings: (state, action) => {
      state.formSettings = action.payload;
    },
    setFormSettingsFetching: (state, action) => {
      state.formSettingsFetching = action.payload;
    },
    setFormSaveFetching: (state, action) => {
      state.formSaveFetching = action.payload;
    },
    setDepartmentsList: (state, action) => {
      state.departmentsList = action.payload;
    },
    setIsHasNote: (state, action) => {
      state.incidentsList = state.incidentsList.map((item) => {
        if (item.incident_id === action.payload) {
          return { ...item, has_note: true };
        }
        return item;
      });
    },
  },
});

export const {
  setIncidentsList,
  setLoading,
  setPagination,
  setSelectedIncidentInfo,
  setIncidentInfoFetching,
  setDeleteIncident,
  setFormSettings,
  setFormSettingsFetching,
  setFormSaveFetching,
  setDepartmentsList,
  setIsHasNote,
} = incidentsReducer.actions;

export default incidentsReducer.reducer;

export const getIncidentsList =
  ({
    page,
    searchQuery,
    start_dt,
    end_dt,
    pageSize,
    type,
    status,
    nerisStatus,
    department,
    responseTime,
  }) =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));

      const response = await incidentsApi.getIncidents({
        page,
        searchQuery,
        start_dt,
        end_dt,
        pageSize,
        type,
        status,
        nerisStatus,
        responseTime,
        department,
      });

      if (response.status === 200) {
        dispatch(setIncidentsList(response.data.data));
        dispatch(setPagination(response.data.pagination));
      } else throw response.response.data;
    } catch (err) {
      toast.error(err.message);
      console.error(err);
    } finally {
      dispatch(setLoading(false));
    }
  };

export const getIncidentInfo = (id) => async (dispatch) => {
  try {
    dispatch(setIncidentInfoFetching(true));
    const response = await incidentsApi.getIncidentInfo(id);

    if (response.status === 200) {
      dispatch(setSelectedIncidentInfo({ ...response.data, id }));
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.error(err);
  } finally {
    dispatch(setIncidentInfoFetching(false));
  }
};

export const uploadIncidentXml = (selectedFile) => async (dispatch) => {
  try {
    const formData = new FormData();
    formData.append('file', selectedFile);

    const response = await incidentsApi.uploadIncidentXml(formData);

    if (response.status === 200) {
      return true;
    } else throw response;
  } catch (err) {
    return err;
  }
};

export const deleteIncident = (id) => async (dispatch) => {
  try {
    const response = await incidentsApi.deleteIncident(id);
    if (response.status === 200 && response.data.status === 'success') {
      toast.success('The incident has been successfully removed.');
      dispatch(setDeleteIncident(id));
    } else throw response;
  } catch (err) {
    toast.error('Failed to delete Incident');
    console.log(err);
  }
};

export const syncNeris = (id) => async (dispatch, getState) => {
  try {
    const response = await incidentsApi.syncNeris(id);
    if (response.status === 200 && response.data.status === 'success') {
      toast.success('Synchronization with neris was successful');
      return true;
      // const { incidentsPagination } = getState().incidentsReducer;
      // const currentPage = incidentsPagination ? incidentsPagination.current_page : 1;
      // const limit = incidentsPagination ? incidentsPagination.per_page : 20;
      // dispatch(getIncidentsList(currentPage, null, null, null, limit, null, null, null, null));
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.log(err);
  }
};

export const getFormSettings = (id) => async (dispatch) => {
  try {
    dispatch(setFormSettingsFetching(true));
    const response = await incidentsApi.formSettings({
      incident: id,
    });
    if (response.status === 200) {
      dispatch(setFormSettings(response.data));
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.log(err);
  } finally {
    dispatch(setFormSettingsFetching(false));
  }
};

export const saveIncidentsForm =
  (id, data, successCb, errorCb) => async (dispatch) => {
    try {
      dispatch(setFormSaveFetching(true));
      const response = await incidentsApi.saveIncidentsForm(id, data);
      if (response.status === 200) {
        successCb?.(response);
      } else throw response.response.data;
    } catch (err) {
      toast.error(err.message);
      console.log(err);
      errorCb?.(err);
    } finally {
      dispatch(setFormSaveFetching(false));
    }
  };

export const getDepartmentsList = (id) => async (dispatch) => {
  try {
    dispatch(setFormSettingsFetching(true));
    const response = await incidentsApi.getDepartmentsList(id);
    if (response.status === 200) {
      dispatch(setDepartmentsList(response.data.departments));
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.log(err);
  } finally {
    dispatch(setFormSettingsFetching(false));
  }
};

export const saveNote = (id, note) => async (dispatch) => {
  try {
    const response = await incidentsApi.saveNotes(id, note);
    if (response.status === 200) {
      toast.success('Note saved successfully');
      dispatch(setIsHasNote(id));
      return true;
    } else throw response.response.data;
  } catch (err) {
    toast.error(err.message);
    console.log(err);
  }
};
